/**
 * El listado de clubes: buscar uno, pedir entrar, y ver los propios.
 *
 * TRES COSAS EN UNA PANTALLA, y en ese orden a propósito:
 *
 *  1. LOS MÍOS ARRIBA. Quien ya juega en un club entra acá para ir A ESE
 *     club, no para descubrir otros. Enterrados entre doscientos, hay que
 *     buscarse a uno mismo en una lista.
 *  2. EL BUSCADOR. Con más de una pantalla de clubes, mirar la lista entera
 *     deja de servir: se busca por nombre o por lugar.
 *  3. PEDIR ENTRAR SIN SALIR DE ACÁ. Antes había que abrir la ficha del club
 *     para encontrar el formulario; ahora se despliega debajo de su tarjeta.
 *     Es la misma `JoinBox` de la ficha —la de verdad, con la elección de
 *     categoría— y no una copia: una segunda versión del formulario es una
 *     segunda versión de las reglas.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Community } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { CommunityCard } from '../components/CommunityCard';
import { AppLayout } from '../components/AppLayout';
import { JoinBox } from '../components/JoinSection';
import { foldForSearch } from '../components/CountryPicker';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function CommunitiesPage(): JSX.Element {
  const { t } = useTranslation();
  const [approved, setApproved] = useState<Community[] | null>(null);
  const [mine, setMine] = useState<Community[]>([]);
  const [query, setQuery] = useState('');
  /** Qué club tiene el formulario abierto. Uno a la vez. */
  const [pidiendo, setPidiendo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      // Las dos en paralelo: son independientes y esperar una para pedir la
      // otra duplica el tiempo de la pantalla sin ninguna razón.
      const [aprobadas, mias] = await Promise.all([api.listCommunities(), api.listMyCommunities()]);
      setApproved(aprobadas);
      setMine(mias);
    } catch (caught) {
      setError(errorMessage(t, caught));
      setApproved([]);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Todos los clubes que hay que dibujar, sin repetir.
   *
   * `listCommunities` trae los aprobados y `listMyCommunities` los míos, que
   * se pisan: un club aprobado del que soy parte viene en las dos. Se juntan
   * por id, quedándose con la versión de «los míos», que es la que trae mi
   * estado de membresía al día.
   */
  const todos = useMemo(() => {
    const porId = new Map<string, Community>();
    for (const community of approved ?? []) porId.set(community.id, community);
    for (const community of mine) porId.set(community.id, community);
    return [...porId.values()];
  }, [approved, mine]);

  const encontrados = useMemo(() => {
    const q = foldForSearch(query);
    if (q === '') return todos;
    // Por nombre Y por lugar: «Grecia» es las dos cosas en este país, y quien
    // busca su cantón espera encontrar los clubes de ahí.
    return todos.filter(
      (c) => foldForSearch(c.name).includes(q) || foldForSearch(c.locationText).includes(q),
    );
  }, [todos, query]);

  // «Míos» es donde ya estoy adentro, o el que yo creé —aunque todavía esté
  // esperando que Tennis Up Club lo apruebe, que es cuando más falta hace verlo—.
  const míos = encontrados.filter((c) => c.myMembershipStatus === 'ACTIVE' || c.isMine);
  const otros = encontrados.filter((c) => !míos.includes(c));

  /** Al pedir entrar, ese club cambia de estado y se muda a «Mis clubes». */
  function alPedir(community: Community): void {
    setMine((actuales) => [community, ...actuales.filter((c) => c.id !== community.id)]);
    setApproved((actuales) =>
      (actuales ?? []).map((c) => (c.id === community.id ? community : c)),
    );
    setPidiendo(null);
  }

  function tarjeta(community: Community): JSX.Element {
    // Solo se ofrece pedir entrar donde de verdad se puede: un club sin
    // aprobar todavía no recibe a nadie, y a donde ya pedí no se pide dos
    // veces —la tarjeta ya dice «Pediste entrar»—.
    const puedePedir =
      community.status === 'APPROVED' && community.myMembershipStatus === null && !community.isMine;

    return (
      <li key={community.id}>
        <CommunityCard community={community} />

        {puedePedir &&
          (pidiendo === community.id ? (
            <JoinBox community={community} onJoined={alPedir} compact />
          ) : (
            <p className="links">
              <button type="button" className="link" onClick={() => setPidiendo(community.id)}>
                {t('communities.askToJoin')}
              </button>
            </p>
          ))}
      </li>
    );
  }

  return (
    <AppLayout>
      {/* CREAR UN CLUB SE FUE A «MÁS».
          Esta pantalla es para BUSCAR club, que es lo que hace casi todo el
          mundo casi siempre; crear uno lo hace una persona una vez en la
          vida. El botón estaba compitiendo con el título de la pantalla por
          la misma línea, y en «Más» está al lado de «Mi club», que es donde
          alguien va cuando piensa en clubes propios. */}
      <h1>{t('communities.title')}</h1>
      <p className="lead">{t('communities.lead')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor="club-search">{t('communities.search')}</label>
        <input
          id="club-search"
          type="search"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {míos.length > 0 && (
        <section>
          <h2>{t('communities.mineTitle')}</h2>
          <ul className="community-list">{míos.map(tarjeta)}</ul>
        </section>
      )}

      <section>
        <h2>{t('communities.allTitle')}</h2>
        {approved === null ? (
          <p className="help">{t('common.loading')}</p>
        ) : todos.length === 0 ? (
          <p className="help">{t('communities.empty')}</p>
        ) : otros.length === 0 ? (
          // Se distingue «no hay clubes» de «tu búsqueda no encontró nada»:
          // son dos situaciones distintas y la segunda tiene arreglo borrando
          // lo que escribiste.
          <p className="help">{t('communities.noneFound')}</p>
        ) : (
          <ul className="community-list">{otros.map(tarjeta)}</ul>
        )}
      </section>
    </AppLayout>
  );
}
